<template>
  <component
    :is="componentType"
    :id="id || undefined"
    ref="buttonRef"
    :aria-label="ariaLabel || undefined"
    :class="[
      'base-button',
      color,
      variant,
      size,
      {
        loading: loading,
        disabled: to && (disabled || loading),
        centered: centered,
        square: square,
        rounded: rounded,
        wide: wide,
        tall: tall,
        wrap: wrap,
      },
    ]"
    v-bind="destination"
    :disabled="to ? undefined : disabled || loading"
    :rel="opensInNewTab ? 'noopener noreferrer' : undefined"
    :tabindex="to && (disabled || loading) ? -1 : undefined"
    :target="opensInNewTab ? '_blank' : undefined"
    :type="!to ? type : undefined"
    @click="handleClick"
  >
    <slot v-if="iconPosition === 'end'" />

    <BaseIcon
      v-if="loading"
      id="spinner"
      class="spinner"
      :size="size"
    />
    <BaseIcon
      v-else-if="icon"
      :id="icon"
      :size="size"
    />

    <slot v-if="iconPosition === 'start'" />
  </component>
</template>

<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue';
import { computed, ref } from 'vue';

import BaseIcon from './BaseIcon.vue';

/**
 * Primary button component for user interactions
 *
 * Provides multiple color variants, styles, and states including loading indicators.
 * Can function as a button, router-link (internal), or anchor tag (external URLs).
 * Automatically detects external URLs and renders as <a> with target="_blank".
 *
 * @example Basic button
 * ```vue
 * <BaseButton color="primary" variant="solid" @click="handleClick">
 *   Save Changes
 * </BaseButton>
 * ```
 *
 * @example With icon and loading state
 * ```vue
 * <BaseButton
 *   color="success"
 *   icon="check"
 *   :loading="isSubmitting"
 *   @click="submit"
 * >
 *   Submit Form
 * </BaseButton>
 * ```
 *
 * @example As internal router link
 * ```vue
 * <BaseButton to="/dashboard" variant="outline">
 *   Go to Dashboard
 * </BaseButton>
 * ```
 *
 * @example As external link
 * ```vue
 * <BaseButton to="https://www.example.com" variant="outline">
 *   Visit Website
 * </BaseButton>
 * ```
 *
 * @see {@link packages/ui-vue/src/components/atoms/BaseButton.vue:1}
 */
export interface BaseButtonProps {
  /** Unique element ID */
  id?: string;
  /** Accessible label for icon-only buttons */
  ariaLabel?: string;
  /** HTML button type */
  type?: 'button' | 'submit' | 'reset';
  /**
   * Color variant affecting button appearance
   *
   * - `primary`, `secondary`, `error`, `warning`, `success`, `info`, `neutral`: Standard semantic colors for typical UI actions and states
   * - `special`: Use for unique or non-standard actions that don't fit usual semantic categories (e.g., promotional actions, feature highlights, context-specific interactions)
   *
   * The `special` color should be used sparingly and only when none of the standard semantic colors are appropriate.
   */
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'success' | 'info' | 'neutral' | 'special';
  /** Visual style variant */
  variant?: 'solid' | 'outline' | 'transparent' | 'light' | 'text';
  /** Size variant affecting padding and font size */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Disables button and prevents interaction */
  disabled?: boolean;
  /** Shows loading spinner and disables interaction */
  loading?: boolean;
  /** Phosphor icon name to display */
  icon?: string;
  /**
   * Position of icon relative to slot content
   * @default 'start'
   */
  iconPosition?: 'start' | 'end';
  /**
   * Link destination (internal or external)
   * - Internal paths ('/dashboard') render as router-link
   * - External URLs ('https://...') render as <a> with target="_blank" by default
   * - Objects ({ name: 'route' }) render as router-link
   */
  to?: string | object | null;
  /**
   * Control whether external links open in a new tab
   * - `true`: Always open in new tab
   * - `false`: Open in same tab
   * - `undefined` (default): Auto-detect (external URLs open in new tab)
   */
  newTab?: boolean;
  /** Centers content with justify-content: center */
  centered?: boolean;
  /** Applies square padding (useful for icon-only buttons) */
  square?: boolean;
  /** Makes button fully rounded */
  rounded?: boolean;
  /** Makes button full width */
  wide?: boolean;
  tall?: boolean;
  /** Allow text to wrap (default: false - text uses white-space: nowrap) */
  wrap?: boolean;
  /** Override the rendered HTML element (e.g. 'div' for appearance-only use inside another interactive element) */
  as?: string;
}

const props = withDefaults(defineProps<BaseButtonProps>(), {
  type: 'button',
  color: 'primary',
  variant: 'solid',
  size: 'md',
  disabled: false,
  loading: false,
  icon: '',
  iconPosition: 'start',
  to: null,
  // Without an explicit default, Vue casts an absent Boolean prop to false and auto-detect never runs.
  newTab: undefined,
  centered: false,
  square: false,
  rounded: false,
  wide: false,
  tall: false,
  wrap: false,
});

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void;
}>();

const buttonRef = ref<HTMLElement | ComponentPublicInstance>();

const isAnchorLink = computed(() => {
  if (!props.to) return false;
  if (typeof props.to !== 'string') return false; // Objects are internal routes
  return /^(https?|mailto|tel|sms|ftp):/.test(props.to);
});

const opensInNewTab = computed(() => {
  // Explicit override takes precedence
  if (props.newTab === true) return true;
  if (props.newTab === false) return false;
  // Auto-detect: external URLs open in new tab
  if (!props.to) return false;
  if (typeof props.to !== 'string') return false;
  return /^(https?|ftp):/.test(props.to);
});

const isInternalLink = computed(() => {
  return !!props.to && !isAnchorLink.value;
});

// RouterLink renders its own resolved href, but fallthrough attrs override it — even `href: undefined`.
// The key must be absent on the router-link branch, which a static `:href` binding can't express.
const destination = computed(() => {
  if (isAnchorLink.value) return { href: props.to };
  if (isInternalLink.value) return { to: props.to };
  return {};
});

const componentType = computed(() => {
  if (props.as) return props.as;
  if (isAnchorLink.value) return 'a';
  if (isInternalLink.value) return 'router-link';
  return 'button';
});

const handleClick = (event: MouseEvent) => {
  if (!props.to) {
    emit('click', event);
  }
  // Remove focus after click to prevent persistent focus outline
  if (buttonRef.value) {
    // When used as router-link, buttonRef is a component instance, not HTMLElement
    const element =
      buttonRef.value instanceof HTMLElement ? buttonRef.value : '$el' in buttonRef.value ? buttonRef.value.$el : null;

    if (element && typeof element.blur === 'function') {
      element.blur();
    }
  }
};
</script>

<style>
.base-button {
  align-items: center;
  background-color: var(--button-bg);
  border-color: var(--button-border);
  border-radius: var(--button-border-radius-left) var(--button-border-radius-right) var(--button-border-radius-right)
    var(--button-border-radius-left);
  border-style: solid;
  border-width: var(--button-border-width-top) var(--button-border-width-right) var(--button-border-width-bottom)
    var(--button-border-width-left);
  color: var(--button-color);
  cursor: pointer;
  display: inline-flex;
  font-weight: var(--font-semi);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  outline-offset: calc(var(--size-border-thin) * 2);
  text-decoration: none;
  transition: var(--transition-default);
  flex-shrink: 0;

  &:is(a, button) {
    font-family: var(--font-body);
  }

  font-size: var(--button-font-size);
  padding-top: var(--button-padding-top, var(--button-padding));
  padding-right: var(--button-padding-right, var(--button-padding));
  padding-bottom: var(--button-padding-bottom, var(--button-padding));
  padding-left: var(--button-padding-left, var(--button-padding));
  gap: var(--button-gap);

  &:disabled,
  &.disabled {
    cursor: not-allowed;
    pointer-events: none;
  }
  &:focus-visible {
    outline: var(--focus-ring-width) solid var(--focus-ring-color);
    outline-offset: var(--focus-ring-offset);
  }
  &.centered {
    justify-content: center;
  }

  /* Sizes */
  &.wide {
    width: 100%;
  }
  &.tall {
    height: 100%;
  }
  &.wrap {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
  }
  &.sm {
    --button-font-size: var(--font-sm);
    --button-padding-top: var(--button-padding-sm-vertical);
    --button-padding-bottom: var(--button-padding-sm-vertical);
    --button-padding-left: var(--button-padding-sm-horizontal);
    --button-padding-right: var(--button-padding-sm-horizontal);
    --button-gap: var(--button-gap-sm);
    &.square {
      --button-padding-top: var(--button-padding-square-sm);
      --button-padding-bottom: var(--button-padding-square-sm);
      --button-padding-left: var(--button-padding-square-sm);
      --button-padding-right: var(--button-padding-square-sm);
    }
  }
  &.md {
    --button-font-size: var(--font-md);
    --button-padding-top: var(--button-padding-md-vertical);
    --button-padding-bottom: var(--button-padding-md-vertical);
    --button-padding-left: var(--button-padding-md-horizontal);
    --button-padding-right: var(--button-padding-md-horizontal);
    --button-gap: var(--button-gap-md);
    &.square {
      --button-padding-top: var(--button-padding-square-md);
      --button-padding-bottom: var(--button-padding-square-md);
      --button-padding-left: var(--button-padding-square-md);
      --button-padding-right: var(--button-padding-square-md);
    }
  }
  &.lg {
    --button-font-size: var(--font-lg);
    --button-padding-top: var(--button-padding-lg-vertical);
    --button-padding-bottom: var(--button-padding-lg-vertical);
    --button-padding-left: var(--button-padding-lg-horizontal);
    --button-padding-right: var(--button-padding-lg-horizontal);
    --button-gap: var(--button-gap-lg);
    &.square {
      --button-padding-top: var(--button-padding-square-lg);
      --button-padding-bottom: var(--button-padding-square-lg);
      --button-padding-left: var(--button-padding-square-lg);
      --button-padding-right: var(--button-padding-square-lg);
    }
  }
  &.xl {
    --button-font-size: var(--font-xl);
    --button-padding-top: var(--button-padding-xl-vertical);
    --button-padding-bottom: var(--button-padding-xl-vertical);
    --button-padding-left: var(--button-padding-xl-horizontal);
    --button-padding-right: var(--button-padding-xl-horizontal);
    --button-gap: var(--button-gap-xl);
    &.square {
      --button-padding-top: var(--button-padding-square-xl);
      --button-padding-bottom: var(--button-padding-square-xl);
      --button-padding-left: var(--button-padding-square-xl);
      --button-padding-right: var(--button-padding-square-xl);
    }
  }

  &.rounded {
    --button-border-radius-left: var(--border-radius-full);
    --button-border-radius-right: var(--border-radius-full);
    --button-border-radius-top: var(--border-radius-full);
    --button-border-radius-bottom: var(--border-radius-full);
  }

  /* States */
  &.loading {
    position: relative;
  }

  /* Color variants - systematic approach using button color utilities */

  &.primary {
    &.light {
      --button-bg: var(--button-primary-light-bg);
      --button-border: var(--button-primary-light-border);
      --button-color: var(--button-primary-light-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-primary-light-active-bg);
        --button-border: var(--button-primary-light-active-border);
        --button-color: var(--button-primary-light-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-primary-light-hover-bg);
        --button-border: var(--button-primary-light-hover-border);
        --button-color: var(--button-primary-light-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-primary-light-disabled-bg);
        --button-border: var(--button-primary-light-disabled-border);
        --button-color: var(--button-primary-light-disabled-color);
      }
    }
    &.outline {
      --button-bg: var(--button-primary-outline-bg);
      --button-border: var(--button-primary-outline-border);
      --button-color: var(--button-primary-outline-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-primary-outline-active-bg);
        --button-border: var(--button-primary-outline-active-border);
        --button-color: var(--button-primary-outline-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-primary-outline-hover-bg);
        --button-border: var(--button-primary-outline-hover-border);
        --button-color: var(--button-primary-outline-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-primary-outline-disabled-bg);
        --button-border: var(--button-primary-outline-disabled-border);
        --button-color: var(--button-primary-outline-disabled-color);
      }
    }
    &.solid {
      --button-bg: var(--button-primary-solid-bg);
      --button-border: var(--button-primary-solid-border);
      --button-color: var(--button-primary-solid-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-primary-solid-active-bg);
        --button-border: var(--button-primary-solid-active-border);
        --button-color: var(--button-primary-solid-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-primary-solid-hover-bg);
        --button-border: var(--button-primary-solid-hover-border);
        --button-color: var(--button-primary-solid-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-primary-solid-disabled-bg);
        --button-border: var(--button-primary-solid-disabled-border);
        --button-color: var(--button-primary-solid-disabled-color);
      }
    }
    &.text,
    &.transparent {
      --button-color: var(--button-primary-transparent-color);
      &:active,
      &:focus-visible {
        --button-color: var(--button-primary-transparent-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-color: var(--button-primary-transparent-hover-color);
      }
      &:disabled {
        --button-color: var(--button-primary-transparent-disabled-color);
      }
    }
    &.transparent {
      --button-bg: var(--button-primary-transparent-bg);
      --button-border: var(--button-primary-transparent-border);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-primary-transparent-active-bg);
        --button-border: var(--button-primary-transparent-active-border);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-primary-transparent-hover-bg);
        --button-border: var(--button-primary-transparent-hover-border);
      }
      &:disabled {
        --button-bg: var(--button-primary-transparent-disabled-bg);
        --button-border: var(--button-primary-transparent-disabled-border);
      }
    }
  }

  &.secondary {
    &.light {
      --button-bg: var(--button-secondary-light-bg);
      --button-border: var(--button-secondary-light-border);
      --button-color: var(--button-secondary-light-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-secondary-light-active-bg);
        --button-border: var(--button-secondary-light-active-border);
        --button-color: var(--button-secondary-light-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-secondary-light-hover-bg);
        --button-border: var(--button-secondary-light-hover-border);
        --button-color: var(--button-secondary-light-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-secondary-light-disabled-bg);
        --button-border: var(--button-secondary-light-disabled-border);
        --button-color: var(--button-secondary-light-disabled-color);
      }
    }
    &.outline {
      --button-bg: var(--button-secondary-outline-bg);
      --button-border: var(--button-secondary-outline-border);
      --button-color: var(--button-secondary-outline-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-secondary-outline-active-bg);
        --button-border: var(--button-secondary-outline-active-border);
        --button-color: var(--button-secondary-outline-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-secondary-outline-hover-bg);
        --button-border: var(--button-secondary-outline-hover-border);
        --button-color: var(--button-secondary-outline-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-secondary-outline-disabled-bg);
        --button-border: var(--button-secondary-outline-disabled-border);
        --button-color: var(--button-secondary-outline-disabled-color);
      }
    }
    &.solid {
      --button-bg: var(--button-secondary-solid-bg);
      --button-border: var(--button-secondary-solid-border);
      --button-color: var(--button-secondary-solid-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-secondary-solid-active-bg);
        --button-border: var(--button-secondary-solid-active-border);
        --button-color: var(--button-secondary-solid-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-secondary-solid-hover-bg);
        --button-border: var(--button-secondary-solid-hover-border);
        --button-color: var(--button-secondary-solid-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-secondary-solid-disabled-bg);
        --button-border: var(--button-secondary-solid-disabled-border);
        --button-color: var(--button-secondary-solid-disabled-color);
      }
    }
    &.text,
    &.transparent {
      --button-color: var(--button-secondary-transparent-color);
      &:active,
      &:focus-visible {
        --button-color: var(--button-secondary-transparent-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-color: var(--button-secondary-transparent-hover-color);
      }
      &:disabled {
        --button-color: var(--button-secondary-transparent-disabled-color);
      }
    }
    &.transparent {
      --button-bg: var(--button-secondary-transparent-bg);
      --button-border: var(--button-secondary-transparent-border);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-secondary-transparent-active-bg);
        --button-border: var(--button-secondary-transparent-active-border);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-secondary-transparent-hover-bg);
        --button-border: var(--button-secondary-transparent-hover-border);
      }
      &:disabled {
        --button-bg: var(--button-secondary-transparent-disabled-bg);
        --button-border: var(--button-secondary-transparent-disabled-border);
      }
    }
  }

  &.error {
    &.light {
      --button-bg: var(--button-error-light-bg);
      --button-border: var(--button-error-light-border);
      --button-color: var(--button-error-light-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-error-light-active-bg);
        --button-border: var(--button-error-light-active-border);
        --button-color: var(--button-error-light-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-error-light-hover-bg);
        --button-border: var(--button-error-light-hover-border);
        --button-color: var(--button-error-light-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-error-light-disabled-bg);
        --button-border: var(--button-error-light-disabled-border);
        --button-color: var(--button-error-light-disabled-color);
      }
    }
    &.outline {
      --button-bg: var(--button-error-outline-bg);
      --button-border: var(--button-error-outline-border);
      --button-color: var(--button-error-outline-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-error-outline-active-bg);
        --button-border: var(--button-error-outline-active-border);
        --button-color: var(--button-error-outline-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-error-outline-hover-bg);
        --button-border: var(--button-error-outline-hover-border);
        --button-color: var(--button-error-outline-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-error-outline-disabled-bg);
        --button-border: var(--button-error-outline-disabled-border);
        --button-color: var(--button-error-outline-disabled-color);
      }
    }
    &.solid {
      --button-bg: var(--button-error-solid-bg);
      --button-border: var(--button-error-solid-border);
      --button-color: var(--button-error-solid-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-error-solid-active-bg);
        --button-border: var(--button-error-solid-active-border);
        --button-color: var(--button-error-solid-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-error-solid-hover-bg);
        --button-border: var(--button-error-solid-hover-border);
        --button-color: var(--button-error-solid-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-error-solid-disabled-bg);
        --button-border: var(--button-error-solid-disabled-border);
        --button-color: var(--button-error-solid-disabled-color);
      }
    }
    &.text,
    &.transparent {
      --button-color: var(--button-error-transparent-color);
      &:active,
      &:focus-visible {
        --button-color: var(--button-error-transparent-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-color: var(--button-error-transparent-hover-color);
      }
      &:disabled {
        --button-color: var(--button-error-transparent-disabled-color);
      }
    }
    &.transparent {
      --button-bg: var(--button-error-transparent-bg);
      --button-border: var(--button-error-transparent-border);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-error-transparent-active-bg);
        --button-border: var(--button-error-transparent-active-border);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-error-transparent-hover-bg);
        --button-border: var(--button-error-transparent-hover-border);
      }
      &:disabled {
        --button-bg: var(--button-error-transparent-disabled-bg);
        --button-border: var(--button-error-transparent-disabled-border);
      }
    }
  }

  &.warning {
    &.light {
      --button-bg: var(--button-warning-light-bg);
      --button-border: var(--button-warning-light-border);
      --button-color: var(--button-warning-light-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-warning-light-active-bg);
        --button-border: var(--button-warning-light-active-border);
        --button-color: var(--button-warning-light-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-warning-light-hover-bg);
        --button-border: var(--button-warning-light-hover-border);
        --button-color: var(--button-warning-light-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-warning-light-disabled-bg);
        --button-border: var(--button-warning-light-disabled-border);
        --button-color: var(--button-warning-light-disabled-color);
      }
    }
    &.outline {
      --button-bg: var(--button-warning-outline-bg);
      --button-border: var(--button-warning-outline-border);
      --button-color: var(--button-warning-outline-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-warning-outline-active-bg);
        --button-border: var(--button-warning-outline-active-border);
        --button-color: var(--button-warning-outline-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-warning-outline-hover-bg);
        --button-border: var(--button-warning-outline-hover-border);
        --button-color: var(--button-warning-outline-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-warning-outline-disabled-bg);
        --button-border: var(--button-warning-outline-disabled-border);
        --button-color: var(--button-warning-outline-disabled-color);
      }
    }
    &.solid {
      --button-bg: var(--button-warning-solid-bg);
      --button-border: var(--button-warning-solid-border);
      --button-color: var(--button-warning-solid-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-warning-solid-active-bg);
        --button-border: var(--button-warning-solid-active-border);
        --button-color: var(--button-warning-solid-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-warning-solid-hover-bg);
        --button-border: var(--button-warning-solid-hover-border);
        --button-color: var(--button-warning-solid-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-warning-solid-disabled-bg);
        --button-border: var(--button-warning-solid-disabled-border);
        --button-color: var(--button-warning-solid-disabled-color);
      }
    }
    &.text,
    &.transparent {
      --button-color: var(--button-warning-transparent-color);
      &:active,
      &:focus-visible {
        --button-color: var(--button-warning-transparent-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-color: var(--button-warning-transparent-hover-color);
      }
      &:disabled {
        --button-color: var(--button-warning-transparent-disabled-color);
      }
    }
    &.transparent {
      --button-bg: var(--button-warning-transparent-bg);
      --button-border: var(--button-warning-transparent-border);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-warning-transparent-active-bg);
        --button-border: var(--button-warning-transparent-active-border);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-warning-transparent-hover-bg);
        --button-border: var(--button-warning-transparent-hover-border);
      }
      &:disabled {
        --button-bg: var(--button-warning-transparent-disabled-bg);
        --button-border: var(--button-warning-transparent-disabled-border);
      }
    }
  }

  &.success {
    &.light {
      --button-bg: var(--button-success-light-bg);
      --button-border: var(--button-success-light-border);
      --button-color: var(--button-success-light-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-success-light-active-bg);
        --button-border: var(--button-success-light-active-border);
        --button-color: var(--button-success-light-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-success-light-hover-bg);
        --button-border: var(--button-success-light-hover-border);
        --button-color: var(--button-success-light-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-success-light-disabled-bg);
        --button-border: var(--button-success-light-disabled-border);
        --button-color: var(--button-success-light-disabled-color);
      }
    }
    &.outline {
      --button-bg: var(--button-success-outline-bg);
      --button-border: var(--button-success-outline-border);
      --button-color: var(--button-success-outline-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-success-outline-active-bg);
        --button-border: var(--button-success-outline-active-border);
        --button-color: var(--button-success-outline-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-success-outline-hover-bg);
        --button-border: var(--button-success-outline-hover-border);
        --button-color: var(--button-success-outline-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-success-outline-disabled-bg);
        --button-border: var(--button-success-outline-disabled-border);
        --button-color: var(--button-success-outline-disabled-color);
      }
    }
    &.solid {
      --button-bg: var(--button-success-solid-bg);
      --button-border: var(--button-success-solid-border);
      --button-color: var(--button-success-solid-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-success-solid-active-bg);
        --button-border: var(--button-success-solid-active-border);
        --button-color: var(--button-success-solid-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-success-solid-hover-bg);
        --button-border: var(--button-success-solid-hover-border);
        --button-color: var(--button-success-solid-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-success-solid-disabled-bg);
        --button-border: var(--button-success-solid-disabled-border);
        --button-color: var(--button-success-solid-disabled-color);
      }
    }
    &.text,
    &.transparent {
      --button-color: var(--button-success-transparent-color);
      &:active,
      &:focus-visible {
        --button-color: var(--button-success-transparent-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-color: var(--button-success-transparent-hover-color);
      }
      &:disabled {
        --button-color: var(--button-success-transparent-disabled-color);
      }
    }
    &.transparent {
      --button-bg: var(--button-success-transparent-bg);
      --button-border: var(--button-success-transparent-border);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-success-transparent-active-bg);
        --button-border: var(--button-success-transparent-active-border);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-success-transparent-hover-bg);
        --button-border: var(--button-success-transparent-hover-border);
      }
      &:disabled {
        --button-bg: var(--button-success-transparent-disabled-bg);
        --button-border: var(--button-success-transparent-disabled-border);
      }
    }
  }

  &.info {
    &.light {
      --button-bg: var(--button-info-light-bg);
      --button-border: var(--button-info-light-border);
      --button-color: var(--button-info-light-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-info-light-active-bg);
        --button-border: var(--button-info-light-active-border);
        --button-color: var(--button-info-light-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-info-light-hover-bg);
        --button-border: var(--button-info-light-hover-border);
        --button-color: var(--button-info-light-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-info-light-disabled-bg);
        --button-border: var(--button-info-light-disabled-border);
        --button-color: var(--button-info-light-disabled-color);
      }
    }
    &.outline {
      --button-bg: var(--button-info-outline-bg);
      --button-border: var(--button-info-outline-border);
      --button-color: var(--button-info-outline-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-info-outline-active-bg);
        --button-border: var(--button-info-outline-active-border);
        --button-color: var(--button-info-outline-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-info-outline-hover-bg);
        --button-border: var(--button-info-outline-hover-border);
        --button-color: var(--button-info-outline-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-info-outline-disabled-bg);
        --button-border: var(--button-info-outline-disabled-border);
        --button-color: var(--button-info-outline-disabled-color);
      }
    }
    &.solid {
      --button-bg: var(--button-info-solid-bg);
      --button-border: var(--button-info-solid-border);
      --button-color: var(--button-info-solid-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-info-solid-active-bg);
        --button-border: var(--button-info-solid-active-border);
        --button-color: var(--button-info-solid-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-info-solid-hover-bg);
        --button-border: var(--button-info-solid-hover-border);
        --button-color: var(--button-info-solid-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-info-solid-disabled-bg);
        --button-border: var(--button-info-solid-disabled-border);
        --button-color: var(--button-info-solid-disabled-color);
      }
    }
    &.text,
    &.transparent {
      --button-color: var(--button-info-transparent-color);
      &:active,
      &:focus-visible {
        --button-color: var(--button-info-transparent-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-color: var(--button-info-transparent-hover-color);
      }
      &:disabled {
        --button-color: var(--button-info-transparent-disabled-color);
      }
    }
    &.transparent {
      --button-bg: var(--button-info-transparent-bg);
      --button-border: var(--button-info-transparent-border);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-info-transparent-active-bg);
        --button-border: var(--button-info-transparent-active-border);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-info-transparent-hover-bg);
        --button-border: var(--button-info-transparent-hover-border);
      }
      &:disabled {
        --button-bg: var(--button-info-transparent-disabled-bg);
        --button-border: var(--button-info-transparent-disabled-border);
      }
    }
  }

  &.neutral {
    &.light {
      --button-bg: var(--button-neutral-light-bg);
      --button-border: var(--button-neutral-light-border);
      --button-color: var(--button-neutral-light-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-neutral-light-active-bg);
        --button-border: var(--button-neutral-light-active-border);
        --button-color: var(--button-neutral-light-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-neutral-light-hover-bg);
        --button-border: var(--button-neutral-light-hover-border);
        --button-color: var(--button-neutral-light-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-neutral-light-disabled-bg);
        --button-border: var(--button-neutral-light-disabled-border);
        --button-color: var(--button-neutral-light-disabled-color);
      }
    }
    &.outline {
      --button-bg: var(--button-neutral-outline-bg);
      --button-border: var(--button-neutral-outline-border);
      --button-color: var(--button-neutral-outline-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-neutral-outline-active-bg);
        --button-border: var(--button-neutral-outline-active-border);
        --button-color: var(--button-neutral-outline-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-neutral-outline-hover-bg);
        --button-border: var(--button-neutral-outline-hover-border);
        --button-color: var(--button-neutral-outline-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-neutral-outline-disabled-bg);
        --button-border: var(--button-neutral-outline-disabled-border);
        --button-color: var(--button-neutral-outline-disabled-color);
      }
    }
    &.solid {
      --button-bg: var(--button-neutral-solid-bg);
      --button-border: var(--button-neutral-solid-border);
      --button-color: var(--button-neutral-solid-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-neutral-solid-active-bg);
        --button-border: var(--button-neutral-solid-active-border);
        --button-color: var(--button-neutral-solid-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-neutral-solid-hover-bg);
        --button-border: var(--button-neutral-solid-hover-border);
        --button-color: var(--button-neutral-solid-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-neutral-solid-disabled-bg);
        --button-border: var(--button-neutral-solid-disabled-border);
        --button-color: var(--button-neutral-solid-disabled-color);
      }
    }
    &.text,
    &.transparent {
      --button-color: var(--button-neutral-transparent-color);
      &:active,
      &:focus-visible {
        --button-color: var(--button-neutral-transparent-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-color: var(--button-neutral-transparent-hover-color);
      }
      &:disabled {
        --button-color: var(--button-neutral-transparent-disabled-color);
      }
    }
    &.transparent {
      --button-bg: var(--button-neutral-transparent-bg);
      --button-border: var(--button-neutral-transparent-border);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-neutral-transparent-active-bg);
        --button-border: var(--button-neutral-transparent-active-border);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-neutral-transparent-hover-bg);
        --button-border: var(--button-neutral-transparent-hover-border);
      }
      &:disabled {
        --button-bg: var(--button-neutral-transparent-disabled-bg);
        --button-border: var(--button-neutral-transparent-disabled-border);
      }
    }
  }

  &.special {
    &.light {
      --button-bg: var(--button-special-light-bg);
      --button-border: var(--button-special-light-border);
      --button-color: var(--button-special-light-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-special-light-active-bg);
        --button-border: var(--button-special-light-active-border);
        --button-color: var(--button-special-light-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-special-light-hover-bg);
        --button-border: var(--button-special-light-hover-border);
        --button-color: var(--button-special-light-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-special-light-disabled-bg);
        --button-border: var(--button-special-light-disabled-border);
        --button-color: var(--button-special-light-disabled-color);
      }
    }
    &.outline {
      --button-bg: var(--button-special-outline-bg);
      --button-border: var(--button-special-outline-border);
      --button-color: var(--button-special-outline-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-special-outline-active-bg);
        --button-border: var(--button-special-outline-active-border);
        --button-color: var(--button-special-outline-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-special-outline-hover-bg);
        --button-border: var(--button-special-outline-hover-border);
        --button-color: var(--button-special-outline-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-special-outline-disabled-bg);
        --button-border: var(--button-special-outline-disabled-border);
        --button-color: var(--button-special-outline-disabled-color);
      }
    }
    &.solid {
      --button-bg: var(--button-special-solid-bg);
      --button-border: var(--button-special-solid-border);
      --button-color: var(--button-special-solid-color);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-special-solid-active-bg);
        --button-border: var(--button-special-solid-active-border);
        --button-color: var(--button-special-solid-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-special-solid-hover-bg);
        --button-border: var(--button-special-solid-hover-border);
        --button-color: var(--button-special-solid-hover-color);
      }
      &:disabled {
        --button-bg: var(--button-special-solid-disabled-bg);
        --button-border: var(--button-special-solid-disabled-border);
        --button-color: var(--button-special-solid-disabled-color);
      }
    }
    &.text,
    &.transparent {
      --button-color: var(--button-special-transparent-color);
      &:active,
      &:focus-visible {
        --button-color: var(--button-special-transparent-active-color);
      }
      &:hover:not(:active, :focus) {
        --button-color: var(--button-special-transparent-hover-color);
      }
      &:disabled {
        --button-color: var(--button-special-transparent-disabled-color);
      }
    }
    &.transparent {
      --button-bg: var(--button-special-transparent-bg);
      --button-border: var(--button-special-transparent-border);
      &:active,
      &:focus-visible {
        --button-bg: var(--button-special-transparent-active-bg);
        --button-border: var(--button-special-transparent-active-border);
      }
      &:hover:not(:active, :focus) {
        --button-bg: var(--button-special-transparent-hover-bg);
        --button-border: var(--button-special-transparent-hover-border);
      }
      &:disabled {
        --button-bg: var(--button-special-transparent-disabled-bg);
        --button-border: var(--button-special-transparent-disabled-border);
      }
    }
  }

  /* Text variant - no padding, background, or border */
  &.text {
    line-height: 1.25;
    --button-padding-top: 0;
    --button-padding-right: 0;
    --button-padding-bottom: 0;
    --button-padding-left: 0;
    --button-bg: transparent;
    --button-border: transparent;
    --button-border-width-top: 0;
    --button-border-width-right: 0;
    --button-border-width-bottom: 0;
    --button-border-width-left: 0;
  }
}
</style>
